import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

interface Props {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  placeholder?: string;
  className?: string;
  emptyHint?: string;
}

/**
 * Multi-select avec recherche au clavier.
 * Tape "sax" → filtre vers "Saxophoniste". Entrée valide la première suggestion.
 */
export default function SearchableMultiSelect({
  label,
  options,
  selected,
  onToggle,
  placeholder = 'Rechercher…',
  className = '',
  emptyHint = 'Aucune option (à configurer dans Paramètres)',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const target = e.target as Node | null;
      if (wrapRef.current && target && !wrapRef.current.contains(target)) {
        setOpen(false);
        setQuery('');
      }
    };
    // capture phase + pointerdown : se déclenche même quand un parent
    // (Dialog Radix, overlay, autre champ) stoppe la propagation des events.
    document.addEventListener('pointerdown', handler, true);
    document.addEventListener('focusin', handler, true);
    return () => {
      document.removeEventListener('pointerdown', handler, true);
      document.removeEventListener('focusin', handler, true);
    };
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filtered[0]) {
      e.preventDefault();
      onToggle(filtered[0]);
      setQuery('');
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className={className} ref={wrapRef}>
      <label className="block text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5">
        {label}
      </label>

      {/* Tags sélectionnés */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selected.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded text-xs"
            >
              {s}
              <button
                type="button"
                onClick={() => onToggle(s)}
                className="hover:text-amber-100"
                aria-label={`Retirer ${s}`}
              >
                <X size={10} strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Champ recherche / déclencheur */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length ? 'Ajouter…' : placeholder}
          className="w-full pl-9 pr-9 py-2.5 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
          aria-label="Ouvrir la liste"
        >
          <ChevronDown size={14} strokeWidth={1.5} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="relative">
          <div className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
            {options.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-3 py-2">{emptyHint}</div>
            ) : filtered.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-3 py-2">Aucun résultat pour « {query} »</div>
            ) : (
              filtered.map((opt) => {
                const isSelected = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => { onToggle(opt); setQuery(''); inputRef.current?.focus(); }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-slate-800/80 border-b border-slate-800/40 last:border-0 ${
                      isSelected ? 'text-amber-300 bg-amber-500/5' : 'text-slate-200'
                    }`}
                  >
                    <span>{opt}</span>
                    {isSelected && <Check size={12} className="text-amber-400" strokeWidth={2} />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
